import { CpuChipIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import AccessoryDrawer from 'components/accessories-page/accessory-drawer';
import Select from 'components/inputs/select';
import { SystemOptionCondenserType } from 'data/types';
import { DuctedLineSetOptionType } from 'utils/ducted-accessories-types';
import { getDuctedIndoorUnits } from 'utils/ducted-line-set-units';
import DuctedLineSetIndoorUnit from './ducted-line-set-indoor-unit';
import DuctedLineSetPhoto from './ducted-line-set-photo';
import DuctedLineSetProductName from './ducted-line-set-product-name';

const NO_LINE_SET_VALUE = 'none';

type DuctedLineSetSelectProps = {
  condenser: SystemOptionCondenserType;
  options: DuctedLineSetOptionType[];
  selectedProductId: number | null;
  recommendedProductId: number | null;
  requiredLengthFeet: number | null;
  onChange: (productId: number | null) => void;
};

const optionLabel = (
  option: DuctedLineSetOptionType,
  isRecommended: boolean
): string => {
  const suffix = isRecommended ? ' (Recommended)' : '';
  return `${option.lengthFeet} ft${suffix}`;
};

const parseSelection = (value: string | number): number | null => {
  if (value === '' || value === NO_LINE_SET_VALUE) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function DuctedLineSetSelect({
  condenser,
  options,
  selectedProductId,
  recommendedProductId,
  requiredLengthFeet,
  onChange
}: DuctedLineSetSelectProps) {
  const selectOptions = [
    ...options.map((option) => ({
      label: optionLabel(
        option,
        option.product.productId === recommendedProductId
      ),
      value: option.product.productId
    })),
    { label: 'No line set needed', value: NO_LINE_SET_VALUE }
  ];
  const selectedOption = options.find(
    (option) => option.product.productId === selectedProductId
  );
  const selectValue =
    selectedProductId === null ? NO_LINE_SET_VALUE : selectedProductId;
  const [zone] = condenser.zones;
  const indoorUnits = getDuctedIndoorUnits(zone);

  return (
    <AccessoryDrawer
      title='Line Set'
      description='Refrigerant lines that run between the outdoor unit and the indoor unit. Choose the shortest length that covers the run between them.'
      itemSelected={selectedProductId !== null}
      notNeeded={selectedProductId === null}
      handleNotNeeded={(notNeeded) =>
        onChange(notNeeded ? null : recommendedProductId)
      }
      switchDisabled={recommendedProductId === null}
      required
    >
      <div className='grid w-full grid-cols-[minmax(0,1fr)_22rem_minmax(0,1fr)] divide-x divide-gray-200'>
        <div className='flex min-w-0 flex-col justify-center gap-3 p-5'>
          <div className='flex items-center gap-2'>
            <CpuChipIcon className='h-6 w-6' />
            <span className='mr-3 text-lg font-bold'>{condenser.name}</span>
          </div>
          <div className='flex items-center divide-x divide-gray-200'>
            <div className='flex items-center gap-4 pr-8'>
              <DuctedLineSetPhoto product={condenser.selectedProduct} />
              <div className='flex flex-col gap-1'>
                <span className='text-xs font-semibold uppercase text-gray-600'>
                  Series
                </span>
                <span className='whitespace-nowrap font-semibold'>
                  {condenser.selectedProduct.series}
                </span>
              </div>
            </div>
            <div className='flex min-w-0 grow basis-0 flex-col gap-1 pl-8'>
              <span className='text-xs font-semibold uppercase text-gray-600'>
                Product
              </span>
              <DuctedLineSetProductName
                type='condenser'
                product={condenser.selectedProduct}
              />
            </div>
          </div>
        </div>
        <div className='flex min-w-0 flex-col'>
          <div className='flex min-h-[150px] grow items-center'>
            <div className='min-w-6 flex-1 border-b-2 border-dashed border-gray-200 pt-1' />
            <div className='flex w-[200px] shrink-0 flex-col gap-2 py-5'>
              <div className='flex flex-col gap-1 text-sm text-gray-600'>
                <span className='text-lg font-bold text-gray-800'>
                  Line set length
                </span>
                {requiredLengthFeet !== null && (
                  <span>
                    Estimated run from the equipment placement:{' '}
                    {requiredLengthFeet} ft
                  </span>
                )}
              </div>
              <Select
                value={selectValue}
                options={selectOptions}
                placeholder='Select line set'
                onChange={(value) => onChange(parseSelection(value))}
                preventDeselection
              />
              {selectedProductId === null && (
                <span className='text-sm text-yellow-600'>
                  The system needs a compatible line set to function properly.
                </span>
              )}
              {selectedOption && (
                <span className='text-sm text-gray-600'>
                  {selectedOption.product.description}
                </span>
              )}
            </div>
            <div className='min-w-6 flex-1 border-b-2 border-dashed border-gray-200 pt-1' />
          </div>
        </div>
        <div className='flex min-w-0 flex-col'>
          {zone && (
            <div className='flex min-h-[150px] grow flex-col justify-center gap-3 p-5'>
              <div className='flex items-center gap-2'>
                <RectangleStackIcon className='h-6 w-6 shrink-0' />
                <span className='text-lg font-bold'>{zone.name}</span>
              </div>
              {indoorUnits.map((unit) => (
                <DuctedLineSetIndoorUnit
                  key={unit.key}
                  product={unit.product}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AccessoryDrawer>
  );
}
